import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { OrderedMap, Map } from 'immutable';
import get from 'lodash/get';

import { imageCdnAsset } from 'lib/common/url-helpers';

import Booking from 'models/booking';
import { ParkingPass as ParkingPassModel } from 'models/parking-passes';
import UserModel from 'models/user';
import {
  SCAN_CODE_METHOD,
  LICENSE_PLATE_METHOD,
  GATE_BUTTON_METHOD,
  PRINTED_PASS_METHOD,
} from 'models/validation';
import Brand from 'models/brand';
import { Location } from 'models/locations';
import Ticket from 'models/ticket';

import PassInfoSection from 'components/parking-passes/validation-section/pass-info-section';
import DownloadAppsSection from 'components/parking-passes/validation-section/download-apps-section';
import ValidationMethod from 'components/parking-passes/validation-section/validation-method';
import PricingCode from 'components/parking-passes/validation-section/pricing-code';
import PrintPass from 'components/parking-passes/validation-section/print-pass';
import PassPageNumber from 'components/parking-passes/validation-section/pass-page-number';
import NameOnPass from 'components/parking-passes/validation-section/name-on-pass';
import * as AppContext from 'lib/app-context';

const propTypes = {
  booking: PropTypes.instanceOf(Booking).isRequired,
  parkingPass: PropTypes.instanceOf(ParkingPassModel).isRequired,
  vehicles: PropTypes.instanceOf(OrderedMap).isRequired,
  ticket: PropTypes.instanceOf(Ticket),
  user: PropTypes.instanceOf(UserModel).isRequired,
  addVehicle: PropTypes.func.isRequired,
  openGate: PropTypes.func.isRequired,
  changeModalState: PropTypes.func.isRequired,
  addMessage: PropTypes.func.isRequired,
  pageIndex: PropTypes.instanceOf(Map).isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  location: PropTypes.instanceOf(Location).isRequired,
  usejs: PropTypes.bool.isRequired,
  appContext: PropTypes.string.isRequired,
};

class ValidationSection extends React.Component {
  get background() {
    const backgroundColor = get(this.props, 'parkingPass.validation.validationColors.background');
    return backgroundColor ? `#${backgroundColor}` : 'radial-gradient(circle at 50% 191%,#00d2c4,#2ab7df 22%,#2ab7df 50%,#004c78)';
  }

  get color() {
    return get(this.props, 'parkingPass.validation.validationColors.font', 'FFF');
  }

  get hasScanCode() {
    const { validation: { requiredMethods, optionalMethods } } = this.props.parkingPass;
    return requiredMethods.get(SCAN_CODE_METHOD) || optionalMethods.get(SCAN_CODE_METHOD);
  }

  renderHeaderCopy() {
    const { parkingPass } = this.props;

    if (parkingPass.validation.requiredMethods.get(LICENSE_PLATE_METHOD)) {
      return (<FormattedMessage
        id="parking-pass.license-plate-header"
        defaultMessage="Spot on! Your License Plate Secures Your Space"
      />);
    }
    return (<FormattedMessage
      id="parking-pass.header"
      defaultMessage="My Parking Pass"
    />);
  }

  render() {
    const {
      booking,
      parkingPass,
      vehicles,
      ticket,
      user,
      addVehicle,
      openGate,
      changeModalState,
      addMessage,
      pageIndex,
      brand,
      location,
      usejs,
      appContext,
    } = this.props;

    const style = {
      background: this.background,
      backgroundSize: '100% 300%',
      color: `#${this.color}`,
    };

    const leftValidationMethods = [];
    const rightValidationMethods = [LICENSE_PLATE_METHOD, GATE_BUTTON_METHOD, PRINTED_PASS_METHOD];

    if (this.hasScanCode && appContext !== AppContext.MOBILE) {
      leftValidationMethods.push(SCAN_CODE_METHOD);
    } else if (this.hasScanCode) {
      rightValidationMethods.push(SCAN_CODE_METHOD);
    }

    const rightClasses = cx("col-xs-12 align-center", {
      "col-md-12": leftValidationMethods.length == 0,
      "col-md-5": leftValidationMethods.length > 0,
    });

    return (
      <div className="parking-pass-validation-section" style={style}>
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              <img
                src={brand.logoLightURL}
                className="visible-print-block vertical-padding-bottom print-grayscale"
                width="190"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <h3 className="print h3-sg text-weight-bold parking-pass-main-title align-center with-vertical-margin-0-lte-sm print-text-left print-without-vertical-margin">
                {this.renderHeaderCopy()}
              </h3>
            </div>
            <div className="col-md-2 hidden-lte-sm">
              <PassPageNumber pageIndex={pageIndex} />
            </div>
          </div>
          <div className="row margin-top-20 margin-top-md-0">
            {leftValidationMethods.length > 0 && <div className="col-xs-12 col-md-3 col-md-offset-2">
              <ValidationMethod
                color={this.color}
                parkingPass={parkingPass}
                vehicles={vehicles}
                ticket={ticket}
                user={user}
                addVehicle={addVehicle}
                openGate={openGate}
                changeModalState={changeModalState}
                addMessage={addMessage}
                usejs={usejs}
                appContext={appContext}
                methods={leftValidationMethods}
              />
            </div>}
            <div className={rightClasses}>
              <h5 className="align-center col-xs-12 h5-sg line-height-extra margin-top-md-0 margin-top-sm-10 text-color-dark-slate-lte-sm text-weight-bold">
                <FormattedMessage
                  id="common.pass-number"
                  defaultMessage="Parking Pass #"
                />
                {parkingPass.id}
              </h5>
              <ValidationMethod
                color={this.color}
                parkingPass={parkingPass}
                vehicles={vehicles}
                ticket={ticket}
                user={user}
                booking={booking}
                addVehicle={addVehicle}
                openGate={openGate}
                changeModalState={changeModalState}
                addMessage={addMessage}
                usejs={usejs}
                appContext={appContext}
                methods={rightValidationMethods}
              />
              <PricingCode parkingPass={parkingPass}/>
              {(!brand.showHeader && !brand.showSimplifiedHeader) &&
                <div className="col-xs-12 background-color-white">
                  <div className="col-xs-offset-3 col-xs-6 padding-bottom-10">
                    <img src={imageCdnAsset('/images/passes/logo-parkwhiz-dark-mobile.png')} alt="Powered by ParkWhiz" />
                  </div>
                </div>
              }
              <NameOnPass parkingPass={parkingPass} />
              <PassInfoSection
                booking={booking}
                brand={brand}
                location={location}
                parkingPass={parkingPass}
              />
            </div>
            <div className="col-xs-12 hidden-lg hidden-md">
              <PrintPass parkingPass={parkingPass} />
              <DownloadAppsSection
                parkingPass={parkingPass}
                booking={booking}
                brand={brand}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ValidationSection.propTypes = propTypes;

export default ValidationSection;
